import { Box, Grid, Paper, Button, Stack, Typography } from "@mui/material"
import { Frame } from "../../components/layouts/Frame"
import { CopyIcon } from "../../assets/svgs/CopyIcon"
import { Editor, Frame as CraftFrame, Element, useEditor } from '@craftjs/core';
import { CButton } from "../../components/craftjs/CButton"
import { CContainer } from "../../components/craftjs/CContainer"
import { CText } from "../../components/craftjs/CText"
import { CCard, CCardBottom, CCardTop } from "../../components/craftjs/CCard"
import { CToolbox } from "../../components/craftjs/CToolbox";
import { CSettingsPanel } from "../../components/craftjs/CSettingsPanel";
import { CImage } from "../../components/craftjs/CImage";
import {Layers} from "@craftjs/layers"
import { CDrawUpModal } from "../../components/craftjs/CDrawUpModal";
import { useEffect, useRef, useState } from "react";
import { CInput } from "../../components/craftjs/CInput";
import { useParams } from "react-router-dom";
import { TemplateData } from "../../types";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../assets/contexts/AuthContext";
import { CLink } from "../../components/craftjs/CLink";
import { CForm } from "../../components/craftjs/CForm";
import { CSelectBox } from "../../components/craftjs/CSelectBox";
import { CFAQ } from "../../components/craftjs/CFAQ";
import { CTable } from "../../components/craftjs/CTable";
import html2canvas from 'html2canvas';
import { CCarousel } from "../../components/craftjs/CCarousel";

interface QueryProviderProps {
  onSaveQuery?: (query: any) => void;
  onSaveActions?: (actions: any) => void;
  onSaveEnabled?: (enabled: any) => void;
}

const QueryProvider = ({ onSaveQuery, onSaveActions, onSaveEnabled }: QueryProviderProps) => {
  const { query, actions, enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  useEffect(() => {
    onSaveQuery && onSaveQuery((currentQuery: any) => {
      if (currentQuery !== query) {
        return query;
      }
      return currentQuery;
    });
  }, [query, onSaveQuery]);

  useEffect(() => {
    onSaveActions && onSaveActions((currentActions: any) => {
      if (currentActions !== actions) {
        return actions;
      }
      return currentActions;
    });
  }, [query, onSaveActions]);

  useEffect(() => {
    onSaveEnabled && onSaveEnabled(enabled);
  }, [enabled, onSaveEnabled]);

  return null;
};

export const Design = () => {
  const { template_id } = useParams();
  const [template, setTemplate] = useState<TemplateData>({});
  const { fetchData } = useAPI();
  const [state, setState] = useState<any>(null);
  const [query, setQuery] = useState<any | null>(null);
  const [actions, setActions] = useState<any | null>(null);
  const isLoading = useRef(false);
  const { isAuthenticated } = useAuth();
  const elementRef = useRef<any>(null);
  const [enabled, setEnabled] = useState(false);
  const [showPlaceholderText, setShowPlaceholderText] = useState(false);

  const onSave = async () => {
    const json = query?.serialize();
    const newTemplate = { ...template, content: json }
    const {errors, data} = await fetchData('/templates/save',  newTemplate, 'POST');
    if (errors) {
      // setError(errors);
    } else {
      await capturePreviewImage();
      setTemplate(data);
    }
  }

  const capturePreviewImage = async () => {
    actions.setOptions((options: any) => (options.enabled = false));
  
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const canvas = await html2canvas(elementRef.current, { allowTaint: true, useCORS: true });
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((blob) => resolve(blob), 'image/png')
      );
  
      if (!blob) {
        console.error('Failed to create blob');
      } else {
        const formData = new FormData();
        formData.append('image', blob, 'design.png');
        formData.append('template_id', template_id ?? '');
  
        const { errors, data } = await fetchData('/templates/save-preview-image', formData, 'POST');
  
        if (errors) {
          throw new Error('Upload failed');
        }
      }
  
    } catch (error: any) {
      console.error(error.message);
    } finally {
      actions.setOptions((options: any) => (options.enabled = true));
    }
  };
  

  const onPreview = async () => {
    const json = query?.serialize();
    const newTemplate = { ...template, content: json }
    const {errors, data} = await fetchData('/templates/save',  newTemplate, 'POST');
    
    const width = 390;
    const height = 667;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const url = `/templates/${template_id}/design-preview`;

    window.open(
      url, 
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
    );
  }

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      isLoading.current = true;

      fetchData(`/templates/${template_id}`).then(({ errors, data }) => {
        isLoading.current = false;
        setTemplate(data);
      }).catch(error => {
        isLoading.current = false;
        console.log(error);
      })
    }
  }, [isAuthenticated, template_id]);

  useEffect(() => {
    if (actions && template && template.content && template.content != '') {
      try {
        const json = JSON.parse(template.content);
        actions.deserialize(json);
      } catch (e: any) {
        console.error(e);
      }
    }
  }, [actions, template])

  const handleContextMenu = (event: any) => {
    event.preventDefault();
  };

  return (
    <Frame title="Build Template" toolbarButtons={(
      <>
      <Button variant="contained" onClick={onPreview} startIcon={<CopyIcon color="#FFFFFF" />}>
        Preview
      </Button>
      <Button variant="contained" onClick={onSave} startIcon={<CopyIcon color="#FFFFFF" />}>
        Save
      </Button>
    </>
    )}>
      <Stack onContextMenu={handleContextMenu} >
        <Box>
          <Editor
            resolver={{
              CButton,
              CContainer,
              CForm,
              CText,
              CInput,
              CSelectBox,
              CCard,
              CCardTop,
              CCardBottom,
              CImage,
              CDrawUpModal,
              CLink,
              CFAQ,
              CTable,
              CCarousel,
            }}
            onNodesChange={(query) => {
              let json = query.serialize();
              const data = JSON.parse(json);
              setShowPlaceholderText(data.ROOT.nodes.length == 0);
            }}
          >
            <Grid container spacing={5} style={{ paddingTop: '10px' }}>
              <QueryProvider onSaveActions={setActions} onSaveQuery={setQuery} onSaveEnabled={setEnabled} />
              <Grid item xs>
                <Stack style={{height: "calc(100vh - 180px)", overflow: "auto"}}>
                  <Stack className={enabled ? "craft-editing" : "craft-preview"} ref={elementRef} sx={{ 
                    width: "375px", 
                    marginLeft: "auto", 
                    marginRight: "auto", 
                    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
                    borderTop: '1px solid #0000000C',
                    marginBottom: '10px',
                    position: "relative",
                  }}>
                    {
                      showPlaceholderText && (
                        <Typography style={{ 
                          position: "absolute",
                          width: "100%",
                          textAlign: "center",
                          padding: "20px",
                          boxSizing: "border-box",
                          color: "#888",
                          zIndex: -1,
                        }}>
                          Drag Elements here to begin
                        </Typography>
                      )
                    }
                    <CraftFrame data={state}>
                      <Element
                        canvas
                        is={CContainer}
                        paddingH={0}
                        paddingV={0}
                        background="#FFFFFF"
                        minHeight="667px"
                        data-cy="root-container"
                      >
                      </Element>
                    </CraftFrame>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Paper className="row" style={{height: "calc(100vh - 180px)", overflow: "auto"}}>
                  <CToolbox />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper className="row" style={{height: "calc(100vh - 180px)", overflow: "auto"}}>
                  <CSettingsPanel />
                </Paper>
              </Grid>
              <Grid item xs={2}>
                <Paper className="row" style={{height: "calc(100vh - 180px)", overflow: "auto"}}>
                  <Box p={1} pb={2}>
                    <Typography>Page Structure</Typography>
                  </Box>
                  <Layers />
                </Paper>
              </Grid>
            </Grid>
          </Editor>
        </Box>
      </Stack>
    </Frame>
  )
}
